import React, { useCallback, useState, useEffect, useRef } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { Box, TextField } from "@mui/material";

/* ✅ FIX: keep libraries OUTSIDE */
const LIBRARIES = ["places", "marker"];

const containerStyle = {
  width: "100%",
  height: "300px",
};

/* ✅ FALLBACK (Guangzhou China) */
const FALLBACK_CENTER = {
  lat: 23.1291,
  lng: 113.2644,
};

const MapPicker = ({ setValue, initialLocation }) => {
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const autocompleteRef = useRef(null);
  const placeInputRef = useRef(null);

  /* =========================
     LOAD GOOGLE MAP
  ========================= */
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  /* =========================
     SET INITIAL LOCATION
  ========================= */
  useEffect(() => {
    if (
      initialLocation?.latitude &&
      initialLocation?.longitude
    ) {
      const lat = Number(initialLocation.latitude);
      const lng = Number(initialLocation.longitude);

      setMarker({ lat, lng });

      setValue("latitude", lat);
      setValue("longitude", lng);
    }
  }, [initialLocation, setValue]);

  /* =========================
     CLEANUP ON UNMOUNT
  ========================= */
  useEffect(() => {
    return () => {
      if (map) {
        setMap(null);
      }
      setMarker(null);
    };
  }, []);

  /* =========================
     MAP CENTER
  ========================= */
  const mapCenter = marker || FALLBACK_CENTER;

  useEffect(() => {
    if (map && marker) {
      map.panTo(marker);
      map.setZoom(15);
    }
  }, [map, marker]);

  /* =========================
     ADDRESS COMPONENT HELPERS
  ========================= */
  const resolveComponents = (place) => {
    const components = place?.addressComponents || place?.address_components || [];
    let city = "";
    let state = "";
    let postal_code = "";

    components.forEach((comp) => {
      const types = comp.types || comp.type || [];
      const name = comp.longName || comp.long_name || "";

      if (!city && (types.includes("locality") || types.includes("postal_town") || types.includes("administrative_area_level_2"))) {
        city = name;
      }
      if (!state && types.includes("administrative_area_level_1")) {
        state = name;
      }
      if (!postal_code && types.includes("postal_code")) {
        postal_code = name;
      }
    });

    return { city, state, postal_code };
  };

  const getAddressLine1FromPlace = (place, placePrediction) => {
    const title =
      place?.name ||
      place?.displayName ||
      (placePrediction?.description || "").split(",")[0] ||
      "";

    const formatted =
      place?.formattedAddress || place?.formatted_address ||
      placePrediction?.description ||
      "";

    if (title && formatted && formatted !== title) {
      return `${title} | ${formatted}`;
    }

    return title || formatted || "";
  };

  const getAddressLine1FromGeocoder = (result) => {
    const placeName = result?.address_components?.find((comp) =>
      comp.types?.includes("establishment") || comp.types?.includes("point_of_interest")
    )?.long_name;

    const formatted = result?.formatted_address || "";

    if (placeName && formatted && formatted !== placeName) {
      return `${placeName} | ${formatted}`;
    }

    return formatted;
  };


  const fillAddressFields = (result, lat, lng) => {
    const { city, state, postal_code } = resolveComponents(result);
    setValue("city", city);
    setValue("state", state);
    setValue("pincode", postal_code);

    if ((!city || !state || !postal_code) && window.google?.maps?.Geocoder) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          let resolvedCity = city;
          let resolvedState = state;
          let resolvedPostal = postal_code;

          results[0].address_components.forEach((comp) => {
            if (!resolvedCity && comp.types.includes("locality")) resolvedCity = comp.long_name;
            if (!resolvedCity && comp.types.includes("administrative_area_level_2")) resolvedCity = comp.long_name;
            if (!resolvedState && comp.types.includes("administrative_area_level_1")) resolvedState = comp.long_name;
            if (!resolvedPostal && comp.types.includes("postal_code")) resolvedPostal = comp.long_name;
          });

          setValue("city", resolvedCity);
          setValue("state", resolvedState);
          setValue("pincode", resolvedPostal);
        }
      });
    }
  };

  const updateAddressFromCoords = (lat, lng) => {
    if (!window.google?.maps?.Geocoder) return;
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const place = results[0];
        setValue("addressLine1", getAddressLine1FromGeocoder(place));
        fillAddressFields(place, lat, lng);
      }
    });
  };

  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarker({ lat, lng });
    setValue("latitude", lat);
    setValue("longitude", lng);
    updateAddressFromCoords(lat, lng);
  };

  /* =========================
     CLICK TO DROP PIN
  ========================= */
  const handleClick = useCallback(
    (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      setMarker({ lat, lng });

      setValue("latitude", lat);
      setValue("longitude", lng);

      /* 🔥 REVERSE GEOCODE */
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const address = results[0];

          let city = "";
          let state = "";
          let postal_code = "";

          address.address_components.forEach((comp) => {
            if (comp.types.includes("locality")) {
              city = comp.long_name;
            }
            if (comp.types.includes("administrative_area_level_1")) {
              state = comp.long_name;
            }
            if (comp.types.includes("postal_code")) {
              postal_code = comp.long_name;
            }
          });

          setValue("addressLine1", getAddressLine1FromGeocoder(address));
          setValue("city", city);
          setValue("state", state);
          setValue("pincode", postal_code);
        }
      });
    },
    [setValue]
  );

  /* =========================
     SEARCH (PLACEAUTOCOMPLETEELEMENT / Autocomplete fallback)
  ========================= */
  const [widgetAutocompleteEnabled, setWidgetAutocompleteEnabled] = useState(false);
  const widgetContainerRef = useRef(null);

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) return;

    const places = window.google.maps.places;
    const hasPlaceAutocompleteElement = typeof places.PlaceAutocompleteElement === "function";
    const hasAutocomplete = typeof places.Autocomplete === "function";

    setWidgetAutocompleteEnabled(hasPlaceAutocompleteElement);

    if (!hasPlaceAutocompleteElement && !hasAutocomplete) {
      console.warn("Google Places autocomplete is not available");
      return;
    }

    let widgetOrAutocomplete;
    let cleanup = () => {};

    if (hasPlaceAutocompleteElement && widgetContainerRef.current) {
      try {
        const placeWidget = new places.PlaceAutocompleteElement({
          placeholder: "Search location...",
          /* optional: includedRegionCodes: ['in'], */
        });

        widgetContainerRef.current.innerHTML = "";
        widgetContainerRef.current.appendChild(placeWidget);

        const onWidgetSelect = async (event) => {
          try {
            const placePrediction = event?.placePrediction;
            if (!placePrediction) return;

            const place = await placePrediction.toPlace();
            if (!place) return;

            await place.fetchFields({
              fields: ["displayName", "formattedAddress", "location", "addressComponents"],
            });

            if (!place?.location) return;

            const lat = place.location.lat();
            const lng = place.location.lng();

            setMarker({ lat, lng });
    setValue("latitude", lat);
    setValue("longitude", lng);
            setValue(
              "addressLine1",
              getAddressLine1FromPlace(place, placePrediction)
            );

            fillAddressFields(place, lat, lng);

            map?.panTo({ lat, lng });
          } catch (error) {
            console.error("Error handling PlaceAutocompleteElement selection", error);
          }
        };

        placeWidget.addEventListener("gmp-select", onWidgetSelect);
        widgetOrAutocomplete = placeWidget;

        cleanup = () => {
          placeWidget.removeEventListener("gmp-select", onWidgetSelect);
          if (widgetContainerRef.current?.contains(placeWidget)) {
            widgetContainerRef.current.removeChild(placeWidget);
          }
        };
      } catch (err) {
        console.error("Failed to initialize PlaceAutocompleteElement", err);
        widgetOrAutocomplete = null;
      }
      }

    if (!widgetOrAutocomplete && hasAutocomplete && placeInputRef.current) {
      try {
        const autocomplete = new places.Autocomplete(placeInputRef.current, {
          fields: ["geometry", "formatted_address", "address_components"],
    });

        const onPlaceChanged = () => {
          const place = autocomplete.getPlace();
          if (!place?.geometry) return;

          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          setMarker({ lat, lng });
          setValue("latitude", lat);
          setValue("longitude", lng);
          setValue(
            "addressLine1",
            getAddressLine1FromPlace(place, { description: place.name })
          );
          window.google.maps.event.clearInstanceListeners(autocomplete);
  };
      } catch (err) {
        console.error("Error initializing Autocomplete", err);
      }
    }

    autocompleteRef.current = widgetOrAutocomplete;

    return () => {
      cleanup();
      autocompleteRef.current = null;
    };
  }, [isLoaded, map, setValue]);

  if (!isLoaded) return <p>Loading map...</p>;

  return (
    <Box>
      {/* 🔍 SEARCH BOX */}
      <Box sx={{ mb: 2 }}>
        {!widgetAutocompleteEnabled && (
        <TextField
            inputRef={placeInputRef}
          fullWidth
          placeholder="Search location..."
        />
        )}
        <div
          ref={widgetContainerRef}
          style={{ display: widgetAutocompleteEnabled ? "block" : "none" }}
        />
      </Box>

      {/* 🗺 MAP */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={marker ? 15 : 10}
        onClick={handleClick}
        onLoad={(mapInstance) => {
          setMap(mapInstance);
          if (marker) {
            mapInstance.panTo(marker);
            mapInstance.setZoom(15);
          }
        }}
        onUnmount={() => setMap(null)}
        options={{
          mapId: "68d159ac9d5e3b1ef384aaf3",
        }}
      >
        {marker && (
          <Marker
            position={marker}
            draggable
            onDragEnd={handleMarkerDragEnd}
          />
        )}
      </GoogleMap>
    </Box>
  );
};

export default MapPicker;
